import { api } from "./client";
import type { ApiResponse } from "./types";

/**
 * Announcements — the "what's happening around you" layer.
 *
 * The type/scope fields are OPEN strings on reads, mirroring the backend
 * schema's own rule: these vocabularies grow, and a closed client union turns
 * a new backend value into a crash instead of a string to display. Writes use
 * the closed lists below because an admin typo should be rejected.
 *
 * The targeting model is the part worth understanding before touching this:
 *
 * - `affinityState` is AFFINITY — it boosts ranking for people from that
 *   state and hides nothing from anyone. Everyone still sees the item.
 * - `requiresLanguage` is the ONLY barrier — a hard filter, legitimate solely
 *   when the thing itself cannot be joined without the language (a Tamil book
 *   club), never for news.
 * - Universal types (india_news, us_india_news, platform) cannot carry a
 *   barrier AT ALL; the server rejects the combination with a 400. A policy
 *   change quietly shown to part of the community is the exact failure the
 *   product exists to avoid.
 */

/** Write-side vocabularies. Reads deliberately stay open strings. */
export const ANNOUNCEMENT_TYPES = [
  "business_opened",
  "community_news",
  "india_news",
  "us_india_news",
  "platform",
  "achievement",
] as const;
export type AnnouncementWriteType = (typeof ANNOUNCEMENT_TYPES)[number];

export const ANNOUNCEMENT_SCOPES = ["metro", "state", "national"] as const;
export type AnnouncementWriteScope = (typeof ANNOUNCEMENT_SCOPES)[number];

/**
 * Types that reach everyone by definition — the server refuses a barrier on
 * these.
 *
 * This list must MIRROR the server's. It is a courtesy that turns a 400 into a
 * disabled field with an explanation; when it drifts, the courtesy becomes a
 * lie and the author meets the rejection anyway, having been told the option
 * was available. `achievement` was missing here for a while after it was added
 * on the server, which is exactly that.
 */
export const UNIVERSAL_TYPES: readonly string[] = [
  "india_news",
  "us_india_news",
  "platform",
  "achievement",
];

export const TYPE_LABELS: Record<string, string> = {
  business_opened: "A business opened",
  community_news: "Community news",
  india_news: "News from India",
  us_india_news: "US–India news",
  platform: "From WeDeshi",
  achievement: "A win",
};

export interface AnnouncementCard {
  id: string;
  /** Open vocabulary — render it, don't switch exhaustively. */
  type: string;
  scope: string;
  title: string;
  summary: string;
  imageUrl: string | null;
  videoUrl: string | null;
  linkUrl: string | null;
  linkLabel: string | null;
  source: string | null;
  city: string | null;
  state: string | null;
  metroCode: string | null;
  /** Boosts ranking for people from this state. Never hides. */
  affinityState: string | null;
  /** The barrier. If you can see the card at all, you clear it. */
  requiresLanguage: string | null;
  isPinned: boolean;
  publishedAt: string;
  provider: {
    id: string;
    name: string;
    slug: string;
    city: string | null;
    state: string | null;
  } | null;
}

export interface AnnouncementDetail extends AnnouncementCard {
  body: string | null;
  expiresAt: string | null;
}

export interface AnnouncementList {
  announcements: AnnouncementCard[];
  meta: { total: number; limit: number; offset: number; hasMore: boolean };
}

/**
 * The public read. Signed-in callers get location-personalised ranking from
 * their profile; the city/state/metroCode params exist for anonymous callers
 * (the landing strip) and for the Lab to impersonate a place.
 */
export function listAnnouncements(params?: {
  type?: string;
  city?: string;
  state?: string;
  metroCode?: string;
  limit?: number;
  offset?: number;
}) {
  return api.get<ApiResponse<AnnouncementList>>(
    "/announcements",
    params as Record<string, string | number | boolean | undefined>,
  );
}

export function getAnnouncement(id: string) {
  return api.get<ApiResponse<AnnouncementDetail>>(`/announcements/${id}`);
}

export interface AnnouncementInput {
  type: AnnouncementWriteType;
  scope?: AnnouncementWriteScope;
  title: string;
  summary: string;
  body?: string;
  imageUrl?: string;
  videoUrl?: string;
  linkUrl?: string;
  linkLabel?: string;
  source?: string;
  metroCode?: string;
  city?: string;
  /** Two-letter US state code — the write boundary canonicalises. */
  state?: string;
  affinityState?: string;
  requiresLanguage?: string;
  isPinned?: boolean;
  /**
   * The scheduler. There is no publish route — visibility is
   * `publishedAt <= now`, so a future timestamp IS the scheduled card.
   * Omitted = publish now.
   */
  publishedAt?: string;
  expiresAt?: string;
}

/* ── Garv reactions — composition, never magnitude ─────────────────── */

/** The five chips. Narrow on purpose: no disagree exists to attach to a
 *  spelling bee. `grateful` is the addition — seva is not an achievement to
 *  congratulate. */
export const GARV_REACTIONS = [
  "proud",
  "congratulating",
  "inspired",
  "excited",
  "grateful",
] as const;

export interface GarvReactionCompositionEntry {
  /** Open vocabulary — render the string, skip what you don't recognise. */
  reaction: string;
  people: number;
  /** A COUNT of distinct US states, never a list. */
  states: number;
}

export interface GarvReactionsView {
  myReaction: string | null;
  composition: GarvReactionCompositionEntry[];
}

export function getAnnouncementReactions(id: string) {
  return api.get<ApiResponse<GarvReactionsView>>(`/announcements/${id}/reactions`);
}

/** One reaction per person — a second chip REPLACES the first. */
export function setAnnouncementReaction(id: string, reaction: string) {
  return api.put<ApiResponse<GarvReactionsView>>(`/announcements/${id}/reaction`, { reaction });
}

export function clearAnnouncementReaction(id: string) {
  return api.delete<ApiResponse<GarvReactionsView>>(`/announcements/${id}/reaction`);
}

/** Admin. The server enforces the no-barrier-on-universal rule with a 400. */
export function createAnnouncement(input: AnnouncementInput) {
  return api.post<ApiResponse<AnnouncementDetail>>("/announcements", input);
}

export function updateAnnouncement(
  id: string,
  input: Partial<AnnouncementInput> & { isActive?: boolean },
) {
  return api.patch<ApiResponse<AnnouncementDetail>>(`/announcements/${id}`, input);
}

/** Admin. Soft — flips isActive off; the row survives. 204 on success. */
export function deactivateAnnouncement(id: string) {
  return api.delete<void>(`/announcements/${id}`);
}

export interface AnnouncementAdminRow extends AnnouncementDetail {
  isActive: boolean;
  createdAt: string;
}

/**
 * The editorial ledger — everything, unfiltered by viewer gating. Retired,
 * future-dated, expired and language-barrier rows all appear; this is also
 * how a retired row gets found for revival.
 */
export function listAnnouncementsAdmin(params?: {
  isActive?: 'true' | 'false';
  limit?: number;
  offset?: number;
}) {
  return api.get<
    ApiResponse<{
      announcements: AnnouncementAdminRow[];
      meta: { total: number; limit: number; offset: number; hasMore: boolean };
    }>
  >('/announcements/admin', params as Record<string, string | number | boolean | undefined>);
}
